import type { StageId } from '../../api/src/programs';
import type { IInstalment } from '../../api/src/workspace';
import type { IOperationLogEntry } from '../../../src/models/Operation';
import type { IRecord } from '../lists/ListStore';
import type { RecordBatch } from '../lists/RecordWriter';
import { eventView, type IEventView, nextDue } from './events';
import type { RecordSet } from './FirmRecords';
import { applicationView, type IApplicationView, itemView, localToday, outstandingCount } from './model';

/**
 * The firm's fee instalments billed when a file reaches this stage: a reminder each, once per file.
 * Called wherever a file's stage changes (the stage menu, recording the submission, the decision, closing).
 */
export async function addInstalments(set: RecordSet, batch: RecordBatch, a: IApplicationView, stage: StageId, instalments: IInstalment[] | undefined, events: IEventView[]): Promise<void> {
  for (const i of (instalments ?? []).filter(x => x.stage === stage)) {
    if (!events.some(e => e.kind === 'instalment' && e.title === i.label)) {
      await batch.create(set.events, { Title: i.label, ApplicationKey: a.key, Kind: 'instalment', Due: localToday() }, i.label, `${i.label} due on ${a.title}`);
    }
  }
}

/**
 * Brings the board's and Today's summaries for one file (items outstanding, next due date) up to date.
 * They're summaries, so the change isn't logged in History. Used after an Undo in History, which
 * can change a checklist or deadline without the application page being open.
 */
export async function refreshSummary(set: RecordSet, applicationKey: string): Promise<void> {
  const application: IRecord | undefined = await set.applications.get(applicationKey);
  if (!application) {
    return;
  }
  const [items, events] = await Promise.all([set.checklist.query({ ApplicationKey: applicationKey }), set.events.query({ ApplicationKey: applicationKey })]);
  const n: number = outstandingCount(items.map(itemView));
  const due: string | undefined = nextDue(events.map(eventView));
  const view: IApplicationView = applicationView(application);
  if (n !== view.outstanding || (due ?? '') !== (view.nextDue ?? '')) {
    await set.applications.update(application.id, { Outstanding: n, NextDue: due });
  }
}

/** The files whose checklist or deadlines a History batch touched. */
export async function filesTouched(set: RecordSet, entries: IOperationLogEntry[]): Promise<string[]> {
  const keys: { [key: string]: boolean } = {};
  for (const store of [set.checklist, set.events]) {
    for (const e of entries.filter(x => x.itemKind === 'record' && x.sourceParentId === store.schema.urlName)) {
      const f = await store.fieldsOf(e.itemId, ['ApplicationKey']);
      if (f?.ApplicationKey) {
        keys[String(f.ApplicationKey)] = true;
      }
    }
  }
  return Object.keys(keys);
}
